import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarHeader, type CalendarMode } from "./CalendarHeader";
import { CalendarGrid } from "./CalendarGrid";
import { CalendarDayColumns } from "./CalendarDayColumns";
import { DayEventsModal } from "./DayEventsModal";
import { EventModal } from "./EventModal";
import {
  formatDayRange,
  formatMonthYear,
  getWeekDays,
  getWeekendDays,
  type CalendarDay,
} from "@/lib/dateUtils";
import type { Event } from "./EventCard";

interface CalendarViewProps {
  events: Event[];
  /** The day the calendar is centred on; which span that means depends on the mode. */
  anchorDate: Date;
  onAnchorChange: (date: Date) => void;
  initialEvent?: Event;
}

export function CalendarView({
  events,
  anchorDate,
  onAnchorChange,
  initialEvent,
}: CalendarViewProps) {
  const [mode, setMode] = useState<CalendarMode>("month");
  const [selectedDay, setSelectedDay] = useState<{
    dateString: string;
    events: Event[];
  } | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const initialEventOpened = useRef(false);

  useEffect(() => {
    if (initialEvent && !initialEventOpened.current) {
      initialEventOpened.current = true;
      setSelectedEvent(initialEvent);
    }
  }, [initialEvent]);

  const days = useMemo<CalendarDay[]>(() => {
    if (mode === "week") return getWeekDays(anchorDate);
    if (mode === "weekend") return getWeekendDays(anchorDate);
    return [];
  }, [mode, anchorDate]);

  const title = mode === "month" ? formatMonthYear(anchorDate) : formatDayRange(days);

  /**
   * Whether "Today" would change anything.
   *
   * For the week and weekend it is not enough to ask whether today is on
   * screen — on a Tuesday the weekend view is already showing the weekend that
   * jumping to today would land on, so the button compares the span itself
   * rather than looking for today inside it.
   */
  const isCurrent = useMemo(() => {
    const today = new Date();
    if (mode === "month") {
      return (
        anchorDate.getFullYear() === today.getFullYear() &&
        anchorDate.getMonth() === today.getMonth()
      );
    }
    const currentSpan = mode === "week" ? getWeekDays(today) : getWeekendDays(today);
    return currentSpan[0]?.dateString === days[0]?.dateString;
  }, [mode, anchorDate, days]);

  const shiftBy = (direction: 1 | -1) => {
    if (mode === "month") {
      onAnchorChange(new Date(anchorDate.getFullYear(), anchorDate.getMonth() + direction, 1));
      return;
    }
    onAnchorChange(
      new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate() + direction * 7)
    );
  };

  const handleDayClick = (dateString: string, dayEvents: Event[]) => {
    if (dayEvents.length > 0) setSelectedDay({ dateString, events: dayEvents });
  };

  const handleEventClick = (event: Event) => {
    setSelectedDay(null);
    setSelectedEvent(event);
  };

  return (
    <>
      <CalendarHeader
        title={title}
        mode={mode}
        onModeChange={setMode}
        onPrev={() => shiftBy(-1)}
        onNext={() => shiftBy(1)}
        onToday={() => onAnchorChange(new Date())}
        isCurrent={isCurrent}
      />

      {mode === "month" ? (
        <CalendarGrid currentMonth={anchorDate} events={events} onDayClick={handleDayClick} />
      ) : (
        <CalendarDayColumns days={days} events={events} onEventClick={handleEventClick} />
      )}

      <DayEventsModal
        dateString={selectedDay?.dateString ?? null}
        events={selectedDay?.events ?? []}
        onClose={() => setSelectedDay(null)}
        onEventClick={handleEventClick}
      />

      <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </>
  );
}
